// ** React Imports
import { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import api from "helpers/ApiClient";
import { toast } from "helpers/toastWrapper";

// ** Custom Components
import LoadingContainer from "components/layout/LoadingContainer";
import TableView from "components/layout/TableView";
import Pagination from "components/layout/Pagination";
import LoginAvatarSmall from "components/userpanel/LoginAvatarSmall";
import DatePresenter from "components/other/DatePresenter";
import Skeleton from "components/layout/Skeleton";
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import VisitorsCount from "../../components/VisitorsCount";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const Vieweds = ({
	route: { path },
	match: {
		params: { page },
	},
}) => {
	const { t } = useTranslation(["userpanel", "common"]);
	const location = useLocation();
	const history = useHistory();

	const [vieweds, setVieweds] = useState({
		data: [],
		pagination: {},
		total: 0,
		loading: true,
	});

	const currentPage = Number(page || 1);

	const { isPhone } = useContext(deviceTypeContext);

	const columns = [
		{
			key: "viever",
			isMain: true,
			headerName: t("common:person"),
			rowCellProps: {
				maxWidth: "200px",
			},
			rowComponent: ({ data } = {}) => {
				return (
					<LoginAvatarSmall
						direction={false}
						data={data}
						additional={() => {
							return (
								isPhone && (
									<>
										<Text color="grey" small fullLine>
											<DatePresenter
												date={data.last_view_date}
												key={data.id}
											/>
										</Text>
										<Text>
											{t(
												"userpanel:visitors.tableHeaders.howManyTimes",
											)}
											:{" "}
											<Text bold>{data.count_view}</Text>
										</Text>
									</>
								)
							);
						}}
					/>
				);
			},
		},
		{
			key: "count",
			showInMain: isPhone,
			headerName: t("userpanel:visitors.tableHeaders.howManyTimes"),
			dataKey: "count_view",
		},
		{
			key: "date",
			showInMain: isPhone,
			headerName: t("userpanel:visitors.tableHeaders.dateOfLastVist"),
			rowComponent: ({ data }) => (
				<DatePresenter date={data.last_view_date} key={data.id} />
			),
		},
	];

	const getVieweds = (currentPage) => {
		setVieweds((prev) => ({ ...prev, loading: true }));
		api.get(`/viewer/viewed`, {
			params: {
				date: "any",
				page: currentPage,
			},
		})
			.then((res) => {
				setVieweds({
					data: res.data?.viewers || [],
					pagination: res.data?.pagination || {},
					total: res.data?.total || 0,
					loading: false,
				});
			})
			.catch((error) => {
				setVieweds((prev) => ({ ...prev, loading: false }));
				if (error?.response?.data?.error) {
					toast.error(`${error.response.data.error}`);
				}
			});
	};

	useEffect(() => {
		if (!location.state?.forceReload) {
			getVieweds(currentPage);
		}
	}, [location.pathname, currentPage]);

	useEffect(() => {
		if (location.state?.forceReload) {
			getVieweds(1);
			history.replace({ ...location, state: null });
		}
	}, [location.key]);

	// #####################################################

	return (
		<Skeleton
			top={
				<Block mb={2}>
					<Text>{t("userpanel:visitors.visitedCount")}:</Text>
					<VisitorsCount counter={vieweds.total} withZero bold />
				</Block>
			}
			main={
				<LoadingContainer
					loading={vieweds.loading}
					ready={!vieweds.loading}
					noContentText={t("userpanel:visitors.viewersEmpty")}
					empty={!vieweds.loading && vieweds.data.length === 0}
					loaderDefault
				>
					<TableView data={vieweds.data} columns={columns} />
				</LoadingContainer>
			}
			pagination={
				vieweds.pagination?.totalpages > 1 &&
				!vieweds.loading && (
					<Pagination
						total={vieweds.pagination.totalpages}
						current={currentPage}
						path={path}
					/>
				)
			}
		/>
	);
};

// #####################################################

export default Vieweds;
